import { BillingProvider, PaymentMethod } from 'api/interfaces'
import { BalanceTransaction } from 'api/interfaces/BalanceTransaction'
import { defaultTransformFn, IBaseQuery, platformApi } from 'api/rtkQueryApi/platform/platformApi'

export interface IGetBalanceTransactionsQuery extends IBaseQuery {
  accountId: string
  fromDate?: string
  toDate?: string
  limit?: number
}

const coreBillingTags = platformApi.enhanceEndpoints({ addTagTypes: ['BillingProvider', 'PaymentMethods', 'Balances', 'Transactions', 'PendingTransactions'] })

export const coreBillingApi = coreBillingTags.injectEndpoints({
  endpoints: build => ({
    getBillingProvider: build.query<BillingProvider, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `billing/billingProvider?accountId=${accountId}`,
        method: 'GET',
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => (result ? [{ type: 'BillingProvider', id: accountId }] : [])
    }),
    getPaymentMethods: build.query<PaymentMethod[], { accountId: string }>({
      query: ({ accountId }) => {
        return {
          url: `paymentMethods?accountId=${accountId}`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'PaymentMethods', id: accountId }],
      keepUnusedDataFor: 1
    }),
    getBalancePaymentMethods: build.query<PaymentMethod[], { accountId: string }>({
      query: ({ accountId }) => {
        return {
          url: `billing/balances?accountId=${accountId}`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'Balances', id: accountId }]
    }),
    getBalanceTransactions: build.query<BalanceTransaction[], IGetBalanceTransactionsQuery>({
      query: ({ accountId, fromDate, toDate, limit, isAgency }) => {
        const params = new URLSearchParams()
        params.append('accountId', accountId)

        if (fromDate) {
          params.append('fromDate', fromDate)
        }

        if (toDate) {
          params.append('toDate', toDate)
        }

        if (limit) {
          params.append('limit', limit.toString())
        }

        return {
          url: `billing/balances/transactions?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'Transactions', id: accountId }]
    }),
    getBalancePendingTransactions: build.query<BalanceTransaction[], IGetBalanceTransactionsQuery>({
      query: ({ accountId, fromDate, toDate, limit, isAgency }) => {
        const params = new URLSearchParams()
        params.append('accountId', accountId)

        if (fromDate) {
          params.append('fromDate', fromDate)
        }

        if (toDate) {
          params.append('toDate', toDate)
        }

        if (limit) {
          params.append('limit', limit.toString())
        }

        return {
          url: `billing/balances/transactions/pending?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'PendingTransactions', id: accountId }]
    })
  }),
  overrideExisting: false
})

export const { useGetBillingProviderQuery, useGetPaymentMethodsQuery, useGetBalancePaymentMethodsQuery, useGetBalanceTransactionsQuery, useGetBalancePendingTransactionsQuery } = coreBillingApi
